import {
    takeEvery,
    call,
    put
} from 'redux-saga/effects';

import { delay } from 'redux-saga';

import api from 'services/restApiHelper';
import ProcessService from 'services/ProcessService';
import DodService from 'services/DodService';
import TabsService from 'services/TabsService';
import DataExplorerService from 'services/DataExplorerService';
import ProcessUtils from 'utils/ProcessUtils';
import NavigationUtils from 'utils/NavigationUtils';
import ProcessValidator from 'utils/ProcessValidator';

const baseUrl = process.env.REACT_APP_BASE_URL; // this set via webpack build during transpile

//tell eslint that Promise is a global var
/*global Promise */

export function* fetchProcessesFlow(action) {
    try {
        // processes are only pushed to local storage when opening a new window
        const processesById = localStorage.getItem('processesById');
        if (processesById) {
            localStorage.removeItem('processesById');
            const response = JSON.parse(processesById);
            yield put({
                type: ProcessService.PROCESSES_FETCH_SUCCESS,
                response
            });

        } else {
            const response = yield api.get(`${baseUrl}/processes`);
            yield put({
                type: ProcessService.PROCESSES_FETCH_SUCCESS,
                response
            });
        }
    } catch (error) {
        yield put({
            type: ProcessService.PROCESSES_FETCH_ERROR,
            error
        });
    }
}

export function* fetchProcessFlow(action) {
    const processId = action.processId;
    const processUI = action.processUI;
    let process;
    try {
        // first see if it's in local storage (being passed from a different window)
        const transferProcess = localStorage.getItem(processId);
        if (transferProcess) {
            localStorage.removeItem(processId);
            process = JSON.parse(transferProcess);
            yield put({
                type: ProcessService.PROCESS_FETCH_SUCCESS,
                response: process,
                processId
            });

        } else {
            const response = yield api.get(`${baseUrl}/processes/${processId}`);
            // There is a timing bug where when reloading a process direct from url, once in awhile the dods have
            // still not yet loaded by the time the process is fetched, which causes the fixProcessModel to crash.
            // So we need to check if the dods have not yet loaded and if so, add a small delay to release control to
            // the fetch dods flow so it can finish.
            let dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
            while (!dodsById) {
                yield delay(100);
                dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
            }

            // We have to load the datastream variables FIRST before we fix the process model or else we won't
            // be able to find all the input datastreams that provide a certain dimension, and hence the transformation
            // type fix logic will be incorrect
            const datastreamVariables = response.datastream_variables;
            yield put({
                type: DodService.DOD_VARS_MULTI_FETCH_SUCCESS,
                response: datastreamVariables
            });

            process = ProcessUtils.fixProcessModel(response.process, processUI);
            yield put({
                type: ProcessService.PROCESS_FETCH_SUCCESS,
                response: process,
                processId,
                processUI
            });
        }
        yield put({
            type: ProcessService.PROCESS_VALIDATE,
            processId,
            process
        });

    } catch (error) {
        console.log(error);
        yield put({
            type: ProcessService.PROCESS_FETCH_ERROR,
            error,
            processId
        });
    }
}

export function* fetchOriginalFlow(action) {
    const processId = action.processId;
    let process;
    try {
        // first see if it's in local storage (being passed from a different window)
        const transferProcess = localStorage.getItem(processId);
        if (transferProcess) {
            localStorage.removeItem(processId);
            process = JSON.parse(transferProcess);
            yield put({
                type: ProcessService.PROCESS_ORIGINAL_FETCH_SUCCESS,
                response: process,
                processId
            });

        } else {
            const response = yield api.get(`${baseUrl}/processes/${processId}`);
            yield put({
                type: ProcessService.PROCESS_ORIGINAL_FETCH_SUCCESS,
                response: response.process,
                processId,
            });
        }

    } catch (error) {
        console.log(error);
        yield put({
            type: ProcessService.PROCESS_FETCH_ERROR,
            error,
            processId
        });
    }
}

export function* fetchProcessRevisionFlow(action) {
    const processId = action.processId;
    const processUI = action.processUI;
    const originalId = action.originalId;
    const revisionNum = action.revisionNum;
    let process;

    try {
        const response = yield api.get(`${baseUrl}/processes/${originalId}/revision/${revisionNum}`);
        // There is a timing bug where when reloading a process direct from url, once in awhile the dods have
        // still not yet loaded by the time the process is fetched, which causes the fixProcessModel to crash.
        // So we need to check if the dods have not yet loaded and if so, add a small delay to release control to
        // the fetch dods flow so it can finish.
        let dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
        while (!dodsById) {
            yield delay(100);
            dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
        }

        // We have to load the datastream variables FIRST before we fix the process model or else we won't
        // be able to find all the input datastreams that provide a certain dimension, and hence the transformation
        // type fix logic will be incorrect
        const datastreamVariables = response.datastream_variables;
        yield put({
            type: DodService.DOD_VARS_MULTI_FETCH_SUCCESS,
            response: datastreamVariables
        });

        process = ProcessUtils.fixProcessModel(response.process, processUI);
        yield put({
            type: ProcessService.PROCESS_REVISION_FETCH_SUCCESS,
            processId,
            revisionNum,
            response: process
        });

        yield put({
            type: ProcessService.PROCESS_VALIDATE,
            processId,
            process
        });

    } catch (error) {
        console.log(error);
        yield put({
            type: ProcessService.PROCESS_REVISION_FETCH_ERROR,
            error,
            processId
        });
    }
}

export function* saveProcessFlow(action) {
    const processId = action.processId;

    //should the process itself be passed in via the action or just read from the store by calling a method on process service???
    const process = action.process;

    try {
        const response = yield api.put(`${baseUrl}/processes/${processId}`, process);
        //const response = yield api.put(`${baseUrl}/processes/${processId}?XDEBUG_SESSION_START=1`, process);
        yield put({
            type: ProcessService.PROCESS_SAVE_SUCCESS,
            response,
            processId: process.name // might not be the same as processId if doing a rename (process.name is the NEW name)
        });

        if (action.saveAsPreviousProcessId) {
            // This was part of a save as, so we have to rename the tab, but not any related tabs
            TabsService.renameTab(action.saveAsPreviousProcessId, process.name, null, true);
        }

        yield put({
            type: ProcessService.PROCESS_VALIDATE,
            processId: process.name,
            process
        });


    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_SAVE_ERROR,
            error,
            processId: process.name // might not be the same as processId if doing a rename
        });
    }
}

export function* saveProcessValidationFlow(action) {
    const processId = action.processId;

    //save the original process with updated ui state validation, but keep working process going
    const originalProcess = action.originalProcess;
    const workingProcess = action.workingProcess;

    try {
        const response = yield api.put(`${baseUrl}/processes/${processId}`, originalProcess);

        yield put({
            type: ProcessService.PROCESS_SAVE_VALIDATION_SUCCESS,
            process: response,
            workingProcess
        });


    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_SAVE_ERROR,
            error,
            processId: originalProcess.name // might not be the same as processId if doing a rename
        });
    }

}

export function* runProcessFlow(action) {
    const processId = action.processId;
    const parameters = action.parameters;
    try {
        const response = yield api.put(`${baseUrl}/processes/${processId}/run`, parameters);

        yield put({
            type: ProcessService.PROCESS_RUN_SUCCESS,
            response,
            processId 
        });

    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_RUN_ERROR,
            error,
            processId
        });
    }
}

export function* updateWithCallbacksFlow(action) {

    // If the process was renamed, then we need to send an event first so the data explorer cache can update before validation occurs
    const processId = action.processId;
    const processName = action.process.name;
    if (processId !== processName) {
        yield put({
            type: ProcessService.PROCESS_RENAME,
            oldName: processId,
            newName: processName
        });
    }

    // Now update the process
    const process = ProcessUtils.fixProcessModel(action.process, action.processUI);

    yield put({
        type: ProcessService.PROCESS_UPDATE_SUCCESS,
        processId: action.processId,
        process: process,
        processUI: action.processUI
    });

    // Call any post-commit callbacks if provided (note callbacks can't modify the process)
    if (action.callbacks) {
        action.callbacks.forEach(callback => {
            callback();
        });
    }

    // Finally, validate
    yield put({
        type: ProcessService.PROCESS_VALIDATE,
        processId: action.process.name, // might not be the same as processId if doing a rename (process.name is the NEW name)
        process
    });

}

export function* validateProcessFlow(action) {
    // Explore if needed (i.e., if process has transform params and some variable data is
    // missing from the existing explorer results)
    const datastreams = DataExplorerService.getDatastreamsThatNeedExploring(action.process);
    const datastreamNames = Object.keys(datastreams);
    const ignoreFiles = [];
    const datastreamExploreRequests = [];
    for (let i = 0; i < datastreamNames.length; i++) {
        const datastreamName = datastreamNames[i];
        const variables = Object.keys(datastreams[datastreamName]);
        let searchFolders = [];
        if (ProcessUtils.hasRunLocations(action.process)) {
            searchFolders = ProcessUtils.getSearchFoldersForExplorer(action.process, datastreamName);
        }
        datastreamExploreRequests.push({
            datastreamName,
            variables,
            ignoreFiles,
            searchFolders
        });
    }

    // Note that this will trigger an action to explore the data, but it will run asynchronously, so it
    // won't hold up the validate flow
    if (datastreamExploreRequests.length > 0) {
        yield put({
            type: DataExplorerService.DATASTREAMS_EXPLORE_START,
            requests: datastreamExploreRequests,
            processId: action.processId,
            process: action.process
        });
    }

    try {
        // First we have to get the list of validated units for this process to compare against
        const unitsToValidate = ProcessValidator.getUnitsToValidate(action.process);
        if (unitsToValidate.length > 0) {
            const unitsString = unitsToValidate.join(';');
            const response = yield api.get(`${baseUrl}/udunits/validate?units=${encodeURIComponent(unitsString)}`);
            const unitsResponse = response.split(';');

            for (let i = 0; i < unitsResponse.length; i++) {
                const unit = unitsToValidate[i];
                const isValid = unitsResponse[i] ? true : false;
                ProcessValidator.addUnitsValidation(unit, isValid);
            }
        }

        try {
            const validationResults =  new ProcessValidator(action.process).validateProcess();
            yield put({
                type: ProcessService.PROCESS_VALIDATE_SUCCESS,
                processId: action.processId,
                revisionNum: action.revisionNum,
                validationResults
            });

        } catch (e) {
            // Note that the process validator does not return a Promise, so we handle any errors differently than a fetch call
            console.log(e.stack);

            yield put({
                type: ProcessService.PROCESS_VALIDATE_ERROR,
                processId: action.processId,
                revisionNum: action.revisionNum
            });
        }

    } catch (error) {
        console.log(error);
        yield put({
            type: ProcessService.PROCESS_VALIDATE_ERROR,
            error,
            processId: action.processId
        });
    }

}

export function* saveAndRunProcessFlow(action) {
    if (action.isDirty) {
        yield* saveProcessFlow(action);
    }
    yield* runProcessFlow(action);
}

export function* fetchProcessOwnersFlow(action) {
    const processId = action.processId;
    try {
        const response = yield api.get(`${baseUrl}/processes/${processId}/owners`);
        yield put({
            type: ProcessService.PROCESS_OWNERS_FETCH_SUCCESS,
            response,
            processId
        });

    } catch (error) {
        console.log(error);
        yield put({
            type: ProcessService.PROCESS_FETCH_ERROR,
            error,
            processId
        });
    }
}

export function* fetchProcessRevisionsFlow(action) {
    const processId = action.processId;
    const originalId = action.originalId;
    try {
        const response = yield api.get(`${baseUrl}/processes/${originalId}/revisions`);
        yield put({
            type: ProcessService.PROCESS_REVISIONS_FETCH_SUCCESS,
            processId,
            response
        });
    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_REVISIONS_FETCH_ERROR,
            error
        });
    }
}

export function* fetchDependencyTreeFlow(action) {
    const id = action.id;

    try {
        const response = yield api.get(`${baseUrl}/dependencies/${id}`);
        yield put({
            type: ProcessService.PROCESS_DEPENDENCIES_FETCH_SUCCESS,
            id,
            response
        });
    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_DEPENDENCIES_FETCH_ERROR,
            error
        });
    }
}

export function* saveProcessOwnersFlow(action) {
    const processId = action.processId;
    const owners = action.owners;

    try {
        const response = yield api.put(`${baseUrl}/processes/${processId}/owners?XDEBUG_SESSION_START=1`, owners);
        yield put({
            type: ProcessService.PROCESS_OWNERS_SAVE_SUCCESS,
            response,
            processId
        });

    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_OWNERS_SAVE_ERROR,
            error,
            processId
        });
    }
}

/**
 * Chain the deletes for multiple processes
 * @param action
 */
export function* deleteProcessesFlow(action) {
    const processIds = action.processIds;

    try {
        const deletes = [];

        // Try to delete all of the processes
        processIds.forEach((processId) => {
            const response = api.delete(`${baseUrl}/processes/${processId}`);
            deletes.push(response);
        });
        const response = yield Promise.all(deletes);

        // Call the success action
        yield put({
            type: ProcessService.PROCESS_DELETE_SUCCESS,
            response,
            processIds
        });

        // Then close tab for all selected tabs
        yield processIds.map((processId) => {
            return call([TabsService, TabsService.closeTab], processId, true);
        });

    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_DELETE_ERROR,
            error,
            processIds
        });
    }
}

export function* newProcessFlow(action) {

    yield put({
        type: ProcessService.PROCESS_NEW_SUCCESS,
        process: action.process,
        processId: action.processId,
    });

    if (action.openProcessInNewTab !== false) {
        NavigationUtils.gotoProcess(action.processId);
    }

    yield put({
        type: ProcessService.PROCESS_VALIDATE,
        processId: action.processId,
        process: action.process
    });
}

export function* getDataConsolidatorRunsFlow(action) {

    const processId = action.processId;

    try {
        const response = yield api.get(`${baseUrl}/processes/${processId}/getruns`);

        yield put({
            type: ProcessService.PROCESS_GET_RUNS_SUCCESS,
            response,
            processId
        });

    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_GET_RUNS_ERROR,
            error,
            processId
        });
    }
}

export function* deleteDataConsolidatorRunsFlow(action) {

    const processId = action.processId;
    const runDate = action.runDate;

    try {
        const response = yield api.delete(`${baseUrl}/processes/${processId}/runs/${runDate}`);

        yield put({
            type: ProcessService.PROCESS_DELETE_RUN_SUCCESS,
            processId,
            runDate
        });

    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_DELETE_RUN_ERROR,
            error,
            processId,
            runDate
        });
    }
}

export function* cancelDataConsolidatorRunsFlow(action) {

    const processId = action.processId;
    const runDate = action.runDate;

    try {
        const response = yield api.put(`${baseUrl}/processes/${processId}/cancel/${runDate}`);

        yield put({
            type: ProcessService.PROCESS_CANCEL_RUN_SUCCESS,
            processId,
            runDate
        });

    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_CANCEL_RUN_ERROR,
            error,
            processId,
            runDate
        });
    }
}

export function* importDataConsolidatorRunOutputFlow(action) {

    const processId = action.processId;
    const runDate = action.runDate;
    const dod = action.dod;

    try {
        // http://pcm-server.local/app_dev.php/processes/barbar8/importdod/2020-05-19_11:44:23.045689?session_id=nj590c48r938abhbvhfg0m1mq0&dod=barbar8output.c1
        const url = `${baseUrl}/processes/${processId}/importdod/${runDate}?dod=${dod}`;
        const response = yield api.put(url);
        const version = JSON.parse(response).version;

        yield put({
            type: ProcessService.PROCESS_IMPORT_RUN_OUPUT_SUCCESS,
            processId,
            runDate,
            dod,
            version
        });

    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_IMPORT_RUN_OUPUT_ERROR,
            error,
            processId,
            runDate,
            dod
        });
    }
}

export function* createAdiProjectFlow(action) {

    const processId = action.processId;
    const parameters = action.parameters;
    
    const username = parameters.username;
    const projectName = parameters.project_name;
    const processType = parameters.process_type;
    try {
        const response = yield api.put(`${baseUrl}/processes/${processId}/create_project`, parameters);

        yield put({
            type: ProcessService.PROCESS_CREATE_PROJECT_SUCCESS,
            response,
            username,
            projectName,
            processType
        });
    } catch (error) {
        yield put({
            type: ProcessService.PROCESS_CREATE_PROJECT_ERROR,
            error
        });
    }
}

export function* watchProcessRequests() {
    yield takeEvery(ProcessService.PROCESSES_FETCH_START, fetchProcessesFlow);
    yield takeEvery(ProcessService.PROCESS_FETCH_START, fetchProcessFlow);
    yield takeEvery(ProcessService.PROCESS_ORIGINAL_FETCH_START, fetchOriginalFlow);
    yield takeEvery(ProcessService.PROCESS_SAVE_START, saveProcessFlow);
    yield takeEvery(ProcessService.PROCESS_DELETE_START, deleteProcessesFlow);
    yield takeEvery(ProcessService.PROCESS_OWNERS_FETCH_START, fetchProcessOwnersFlow);
    yield takeEvery(ProcessService.PROCESS_OWNERS_SAVE_START, saveProcessOwnersFlow);
    yield takeEvery(ProcessService.PROCESS_REVISIONS_FETCH_START, fetchProcessRevisionsFlow);
    yield takeEvery(ProcessService.PROCESS_REVISION_FETCH_START, fetchProcessRevisionFlow);
    yield takeEvery(ProcessService.PROCESS_DEPENDENCIES_FETCH_START, fetchDependencyTreeFlow);
    yield takeEvery(ProcessService.PROCESS_RUN_START, saveAndRunProcessFlow);
    yield takeEvery(ProcessService.PROCESS_UPDATE_WITH_CALLBACKS, updateWithCallbacksFlow);
    yield takeEvery(ProcessService.PROCESS_NEW, newProcessFlow);
    yield takeEvery(ProcessService.PROCESS_VALIDATE, validateProcessFlow);
    yield takeEvery(ProcessService.PROCESS_SAVE_VALIDATION_START, saveProcessValidationFlow);
    yield takeEvery(ProcessService.PROCESS_GET_RUNS_START, getDataConsolidatorRunsFlow);
    yield takeEvery(ProcessService.PROCESS_DELETE_RUN_START, deleteDataConsolidatorRunsFlow);
    yield takeEvery(ProcessService.PROCESS_CANCEL_RUN_START, cancelDataConsolidatorRunsFlow);
    yield takeEvery(ProcessService.PROCESS_IMPORT_RUN_OUPUT_START, importDataConsolidatorRunOutputFlow);
    yield takeEvery(ProcessService.PROCESS_CREATE_PROJECT_START, createAdiProjectFlow);
}



// WEBPACK FOOTER //
// ./src/services/sagas/ProcessServiceSagas.js